import { Flex, Text } from "@chakra-ui/react";
import { t } from "i18next";
import { cloneDeep } from "lodash";
import { useCallback } from "react";
import {
  DefaultNotificationSettingsDto,
  SupplierNotificationReceiversDto,
} from "../../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../../common/auth/useLoggedInWithOrgContextState";
import { useApiError } from "../../../common/errors/useApiError";
import { useSaveOrganization } from "../../organizations/current/useOrganizationEditor";
import { DeleteSettingButton } from "./DeleteSettingButton";
import { initSettings } from "./NotificationSettings";
import { useReceiverRoleNames } from "./useReceiverRoleNames";

type ReceiverType = "inviteReceivers" | "messageReceivers";

export const hasReceivers = (receivers: SupplierNotificationReceiversDto | null | undefined): boolean =>
  !!receivers && (receivers.persons.length > 0 || receivers.organizationRoles.length > 0);

export const SupplierNotificationReceiverSetting = ({
  receivers,
  receiverType,
}: {
  receivers: SupplierNotificationReceiversDto;
  receiverType: ReceiverType;
}) => {
  const roleName = useReceiverRoleNames();
  const authState = useLoggedInWithOrgContextState();
  const { trigger } = useApiError();

  const { saveOrganization, isSaving } = useSaveOrganization();

  const deleteSetting = useCallback(async () => {
    const settings: DefaultNotificationSettingsDto =
      cloneDeep(authState.selectedOrg.defaultNotificationSettings) ?? initSettings();
    if (settings.supplierNotificationSettings) {
      settings.supplierNotificationSettings[receiverType] = {
        persons: [],
        organizationRoles: [],
      };
    }
    try {
      await saveOrganization({ defaultNotificationSettings: settings });
    } catch (e) {
      trigger(e);
    }
  }, [authState.selectedOrg.defaultNotificationSettings, trigger, receiverType, saveOrganization]);

  if (!hasReceivers(receivers)) return null;

  return (
    <Flex flexDirection={"column"} fontSize={"sm"} backgroundColor={"gray.50"} rounded="md" p="2" my="1">
      <Flex justifyContent={"space-between"} width="full">
        <Text fontWeight={"bold"}>{t("Receivers")}</Text>
        <DeleteSettingButton isLoading={isSaving} onClick={() => deleteSetting()} />
      </Flex>
      {receivers.persons.length > 0 && (
        <>
          <Text>{t("Individual receivers")}</Text>
          {receivers.persons.map((person) => (
            <Text key={person.id} fontWeight={"bold"}>
              {person.firstName} {person.lastName}
            </Text>
          ))}
        </>
      )}
      {receivers.organizationRoles.length > 0 && (
        <>
          <Text>{t("Organization role receivers")}</Text>
          {receivers.organizationRoles.map((role) => (
            <Text key={role} fontWeight={"bold"}>
              {roleName.organizationRole(role)}
            </Text>
          ))}
        </>
      )}
    </Flex>
  );
};
